// PODetail.jsx
// Purchase order detail page with header, lines, and actions

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import PurchasingAPI from '../../../services/purchasingService';
import ScopeService from '../../../services/scopeService';
import StatusBadge from './StatusBadge';
import POLineEditor from './POLineEditor';
import ReceiveInventory from '../inventory/ReceiveInventory';

import { getLocationName } from '../../../util/locationUtils';
import { convertFromUTC } from '../../../util/convert_timezone';
import { toast } from 'react-toastify';
import { formatProductLabel } from '../../../util/productLabel';

const DESKTOP_BREAKPOINT = 1024;

class PODetail extends Component {
    state = {
        po: null,
        lines: [],

        // Header edit
        editingHeader: false,
        headerForm: {},

        // Line editing
        addingLine: false,
        editingLineId: null,
        editLineForm: {},

        loading: true,
        saving: false,
        isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT,
        error: null,
        showReceiveModal: false
    };

    async componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        await this.loadData();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
        this.setState({ isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT });
    };

    loadData = async () => {
        const poId = this.props.match.params.id;
        try {
            const poData = await PurchasingAPI.getPurchaseOrder(poId);
            const raw = Array.isArray(poData) ? poData[0] : poData;
            // Normalize: server may return `id` instead of `purchase_order_id`
            const po = { ...raw, purchase_order_id: raw.purchase_order_id || raw.id };
            const lines = (po.lines || []).map(l => ({
                ...l,
                line_id: l.line_id || l.id
            }));

            this.setState({
                po,
                lines,
                headerForm: { ...po },
                loading: false
            });
        } catch (err) {
            console.error('Error loading PO:', err);
            this.setState({ error: 'Failed to load purchase order.', loading: false });
        }
    };

    // Header actions
    handleSaveHeader = async () => {
        const { headerForm, po } = this.state;
        this.setState({ saving: true });
        try {
            await PurchasingAPI.updatePurchaseOrder(po.purchase_order_id, {
                expected_date: headerForm.expected_date || null,
                notes: headerForm.notes || null
            });
            await this.loadData();
            this.setState({ editingHeader: false, saving: false });
        } catch (err) {
            console.error('Error updating PO:', err);
            this.setState({ saving: false });
        }
    };

    buildMailtoUrl = () => {
        const { po, lines } = this.state;
        const company = ScopeService.getCompany() || {};
        const companyName = company.Installer_Name || company.installer_name || '';
        const poNumber = po.po_number || `PO-${po.purchase_order_id}`;
        const vendorEmail = po.vendor_email || po.contact_email || '';

        const subject = `Purchase Order ${poNumber} from ${companyName}`;

        let body = `Purchase Order: ${poNumber}\n`;
        body += `Date: ${po.created_at ? convertFromUTC(po.created_at).format('MM/DD/YYYY') : 'N/A'}\n`;
        if (po.expected_date) {
            body += `Expected Delivery: ${convertFromUTC(po.expected_date).format('MM/DD/YYYY')}\n`;
        }
        body += `\nItems:\n`;

        let grandTotal = 0;
        lines.forEach(line => {
            const lineTotal = (parseFloat(line.quantity_ordered) || 0) * (parseFloat(line.unit_cost) || 0);
            grandTotal += lineTotal;
            const sku = line.vendor_sku ? ` (SKU: ${line.vendor_sku})` : '';
            const cost = line.unit_cost ? ` — $${parseFloat(line.unit_cost).toFixed(2)} ea` : '';
            const total = lineTotal > 0 ? ` — $${lineTotal.toFixed(2)}` : '';
            body += `- ${formatProductLabel(line)}${sku} — Qty: ${line.quantity_ordered || 0}${cost}${total}\n`;
        });

        body += `\nTotal: $${grandTotal.toFixed(2)}\n`;
        if (po.notes) {
            body += `\nNotes: ${po.notes}\n`;
        }
        body += `\nPlease confirm receipt of this order.\n`;

        const installerEmail = company.installer_email || '';

        return `mailto:${encodeURIComponent(vendorEmail)}?cc=${encodeURIComponent(installerEmail)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    handleSubmitPO = async () => {
        if (!window.confirm('Submit this purchase order?')) return;
        this.setState({ saving: true });
        try {
            await PurchasingAPI.submitPO(this.state.po.purchase_order_id);

            // Open email client with PO details
            window.location.href = this.buildMailtoUrl();

            // Also download the PDF as a backup
            const pdf = await this.generatePOPdf();
            const poNumber = this.state.po.po_number || `PO-${this.state.po.purchase_order_id}`;
            pdf.save(`${poNumber}.pdf`);

            toast.success('Purchase order submitted. PDF saved to downloads.');
            await this.loadData();
            this.setState({ saving: false });
        } catch (err) {
            console.error('Error submitting PO:', err);
            const serverMsg = err?.response?.data?.error || err?.response?.data?.message || '';
            toast.error(serverMsg || 'Failed to submit purchase order.');
            this.setState({ saving: false });
        }
    };

    generatePOPdf = async () => {
        const { po, lines } = this.state;
        const { locations } = this.props;
        const company = ScopeService.getCompany() || {};
        const poNumber = po.po_number || `PO-${po.purchase_order_id}`;
        const locationName = po.location_id
            ? getLocationName((locations || []).find(l => l.location_id === po.location_id) || {})
            : '';

        // Build line items rows
        let linesHTML = '';
        let grandTotal = 0;
        lines.forEach(line => {
            const lineTotal = (parseFloat(line.quantity_ordered) || 0) * (parseFloat(line.unit_cost) || 0);
            grandTotal += lineTotal;
            linesHTML += `
                <tr>
                    <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px;">${line.brand || ''}</td>
                    <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px;">${line.product_name || line.name || ''}</td>
                    <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px;">${line.vendor_sku || ''}</td>
                    <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; text-align: center;">${line.quantity_ordered || 0}</td>
                    <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; text-align: right;">${line.unit_cost ? '$' + parseFloat(line.unit_cost).toFixed(2) : ''}</td>
                    <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; text-align: right; font-weight: 500;">${lineTotal > 0 ? '$' + lineTotal.toFixed(2) : ''}</td>
                </tr>`;
        });

        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '-9999px';
        tempDiv.style.width = '800px';
        tempDiv.style.backgroundColor = 'white';
        tempDiv.style.padding = '40px';

        tempDiv.innerHTML = `
            <div style="font-family: Arial, sans-serif; color: #333;">
                <!-- Header -->
                <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px;">
                    <div>
                        <div style="font-size: 22px; font-weight: bold; color: #1f2937;">${company.Installer_Name || company.installer_name || 'Purchase Order'}</div>
                        ${company.installer_address_line1 ? `<div style="font-size: 11px; color: #6b7280; margin-top: 4px;">${company.installer_address_line1}</div>` : ''}
                        ${company.installer_address_city ? `<div style="font-size: 11px; color: #6b7280;">${company.installer_address_city}, ${company.installer_address_state || ''} ${company.installer_address_zip || ''}</div>` : ''}
                        ${company.installer_phone ? `<div style="font-size: 11px; color: #6b7280;">${company.installer_phone}</div>` : ''}
                        ${company.installer_email ? `<div style="font-size: 11px; color: #6b7280;">${company.installer_email}</div>` : ''}
                    </div>
                    <div style="text-align: right;">
                        <div style="font-size: 28px; font-weight: bold; color: #3FB2BE;">PURCHASE ORDER</div>
                        <div style="font-size: 16px; font-weight: 600; color: #374151; margin-top: 4px;">${poNumber}</div>
                        ${po.revision && po.revision > 1 ? `<div style="font-size: 11px; color: #6b7280;">Revision ${po.revision}</div>` : ''}
                    </div>
                </div>

                <!-- Info Grid -->
                <div style="display: flex; justify-content: space-between; margin-bottom: 24px; gap: 24px;">
                    <div style="flex: 1; background: #f8fafc; padding: 12px; border-left: 3px solid #3FB2BE;">
                        <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; font-weight: 600; margin-bottom: 8px;">Vendor</div>
                        <div style="font-size: 13px; font-weight: 600;">${po.vendor_name || ''}</div>
                        ${po.vendor_address ? `<div style="font-size: 11px; color: #6b7280; margin-top: 2px;">${po.vendor_address}</div>` : ''}
                        ${po.vendor_email || po.contact_email ? `<div style="font-size: 11px; color: #6b7280; margin-top: 2px;">${po.vendor_email || po.contact_email}</div>` : ''}
                        ${po.vendor_phone || po.contact_phone ? `<div style="font-size: 11px; color: #6b7280; margin-top: 2px;">${po.vendor_phone || po.contact_phone}</div>` : ''}
                    </div>
                    <div style="flex: 1; background: #f8fafc; padding: 12px; border-left: 3px solid #3FB2BE;">
                        <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; font-weight: 600; margin-bottom: 8px;">Details</div>
                        <div style="font-size: 11px; margin-bottom: 4px;"><span style="font-weight: 600; display: inline-block; width: 110px;">Date:</span> ${po.created_at ? convertFromUTC(po.created_at).format('MM/DD/YYYY') : ''}</div>
                        <div style="font-size: 11px; margin-bottom: 4px;"><span style="font-weight: 600; display: inline-block; width: 110px;">Expected Delivery:</span> ${po.expected_date ? convertFromUTC(po.expected_date).format('MM/DD/YYYY') : 'TBD'}</div>
                        ${locationName ? `<div style="font-size: 11px; margin-bottom: 4px;"><span style="font-weight: 600; display: inline-block; width: 110px;">Ship To Location:</span> ${locationName}</div>` : ''}
                    </div>
                </div>

                <!-- Line Items Table -->
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 16px;">
                    <thead>
                        <tr>
                            <th style="background: #374151; color: white; padding: 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; text-align: left;">Brand</th>
                            <th style="background: #374151; color: white; padding: 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; text-align: left;">Product</th>
                            <th style="background: #374151; color: white; padding: 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; text-align: left;">Vendor SKU</th>
                            <th style="background: #374151; color: white; padding: 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; text-align: center;">Qty</th>
                            <th style="background: #374151; color: white; padding: 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; text-align: right;">Unit Cost</th>
                            <th style="background: #374151; color: white; padding: 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; text-align: right;">Line Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${linesHTML}
                    </tbody>
                </table>

                <!-- Totals -->
                <div style="display: flex; justify-content: flex-end; margin-bottom: 24px;">
                    <div style="width: 250px; background: #f8fafc; padding: 12px;">
                        <div style="display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 4px;">
                            <span style="color: #6b7280;">Lines:</span>
                            <span style="font-weight: 500;">${lines.length}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; font-size: 14px; font-weight: 700; border-top: 2px solid #374151; padding-top: 8px; margin-top: 8px;">
                            <span>Total:</span>
                            <span>$${grandTotal.toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                ${po.notes ? `
                <div style="margin-bottom: 24px;">
                    <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; font-weight: 600; margin-bottom: 4px;">Notes</div>
                    <div style="font-size: 11px; color: #374151; background: #f8fafc; padding: 10px; border-left: 3px solid #e5e7eb;">${po.notes}</div>
                </div>
                ` : ''}
            </div>
        `;

        document.body.appendChild(tempDiv);

        try {
            const canvas = await html2canvas(tempDiv, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const imgWidth = 190;
            const pageHeight = 277;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 10;

            pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight + 10;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            return pdf;
        } finally {
            if (document.body.contains(tempDiv)) {
                document.body.removeChild(tempDiv);
            }
        }
    };

    handleDownloadPdf = async () => {
        try {
            const pdf = await this.generatePOPdf();
            const poNumber = this.state.po.po_number || `PO-${this.state.po.purchase_order_id}`;
            pdf.save(`${poNumber}.pdf`);
        } catch (err) {
            console.error('Error generating PDF:', err);
            toast.error('Failed to generate PDF.');
        }
    };

    handleCancelPO = async () => {
        const { po } = this.state;
        const wasSubmitted = po.status === 'submitted';
        if (!window.confirm('Void this purchase order? This cannot be undone.')) return;
        this.setState({ saving: true });
        try {
            await PurchasingAPI.cancelPO(po.purchase_order_id);
            toast.info('Purchase order voided.');

            // If it was already sent to the vendor, open cancellation email
            if (wasSubmitted) {
                const company = ScopeService.getCompany() || {};
                const companyName = company.Installer_Name || company.installer_name || '';
                const installerEmail = company.installer_email || '';
                const vendorEmail = po.vendor_email || po.contact_email || '';
                const poNumber = po.po_number || `PO-${po.purchase_order_id}`;

                const subject = `CANCELLED — Purchase Order ${poNumber} from ${companyName}`;
                let body = `This is to notify you that Purchase Order ${poNumber} has been cancelled.\n\n`;
                body += `Please disregard any previous correspondence regarding this order.\n\n`;
                body += `If you have any questions, please contact us.\n`;

                window.location.href = `mailto:${encodeURIComponent(vendorEmail)}?cc=${encodeURIComponent(installerEmail)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            }

            await this.loadData();
            this.setState({ saving: false });
        } catch (err) {
            console.error('Error cancelling PO:', err);
            this.setState({ saving: false });
        }
    };

    // Line actions
    handleAddLine = async (lineData) => {
        this.setState({ saving: true });
        try {
            // If the same vendor product is already on the PO, add to its quantity
            const existing = this.state.lines.find(l =>
                String(l.vendor_product_id) === String(lineData.vendor_product_id)
            );
            if (existing) {
                await PurchasingAPI.updatePOLine(existing.line_id, {
                    quantity_ordered: (existing.quantity_ordered || 0) + lineData.quantity_ordered,
                    unit_cost: lineData.unit_cost ?? existing.unit_cost,
                    notes: lineData.notes || existing.notes || null
                });
            } else {
                await PurchasingAPI.addPOLine(this.state.po.purchase_order_id, lineData);
            }
            await this.loadData();
            this.setState({ addingLine: false, saving: false });
        } catch (err) {
            console.error('Error adding line:', err);
            this.setState({ saving: false });
        }
    };

    handleStartEditLine = (line) => {
        this.setState({
            editingLineId: line.line_id,
            editLineForm: {
                quantity_ordered: line.quantity_ordered,
                unit_cost: line.unit_cost || '',
                notes: line.notes || ''
            }
        });
    };

    handleSaveEditLine = async () => {
        const { editingLineId, editLineForm } = this.state;
        this.setState({ saving: true });
        try {
            await PurchasingAPI.updatePOLine(editingLineId, {
                quantity_ordered: parseInt(editLineForm.quantity_ordered, 10),
                unit_cost: editLineForm.unit_cost ? parseFloat(editLineForm.unit_cost) : null,
                notes: editLineForm.notes || null
            });
            await this.loadData();
            this.setState({ editingLineId: null, saving: false });
        } catch (err) {
            console.error('Error updating line:', err);
            this.setState({ saving: false });
        }
    };

    handleDeleteLine = async (lineId) => {
        if (!window.confirm('Remove this line?')) return;
        try {
            await PurchasingAPI.deletePOLine(lineId);
            await this.loadData();
        } catch (err) {
            console.error('Error deleting line:', err);
        }
    };

    getTotals = () => {
        const { lines } = this.state;
        const totalLines = lines.length;
        const totalQty = lines.reduce((sum, l) => sum + (parseInt(l.quantity_ordered, 10) || 0), 0);
        const totalCost = lines.reduce((sum, l) => sum + ((parseFloat(l.quantity_ordered) || 0) * (parseFloat(l.unit_cost) || 0)), 0);
        return { totalLines, totalQty, totalCost };
    };

    render() {
        const { po, lines, loading, saving, error, isDesktop,
                editingHeader, headerForm, addingLine, editingLineId, editLineForm } = this.state;
        const { locations } = this.props;

        if (loading) {
            return (
                <div className="text-center py-5">
                    <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
                    <p className="mt-3">Loading purchase order...</p>
                </div>
            );
        }

        if (error || !po) {
            return <div className="alert alert-danger m-3">{error || 'Purchase order not found.'}</div>;
        }

        const isDraft = po.status === 'draft';
        const canReceive = po.status === 'submitted' || po.status === 'partial';
        const isReadOnly = po.status === 'received' || po.status === 'cancelled';
        const totals = this.getTotals();

        return (
            <div className={`po-detail-container ${!isDesktop ? 'mobile' : ''}`}>
                <style>{`
                    .po-detail-container {
                        max-width: 1400px;
                        margin: 0 auto;
                        padding: var(--space-6);
                        background: var(--color-bg-panel);
                        min-height: 100vh;
                    }
                    .po-detail-container.mobile { padding: var(--space-4); }
                    .po-info-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                        gap: var(--space-4);
                        margin-bottom: var(--space-4);
                    }
                    .po-info-item label {
                        display: block;
                        font-size: var(--font-size-xs);
                        text-transform: uppercase;
                        letter-spacing: var(--letter-spacing-uppercase);
                        color: var(--color-text-secondary);
                        font-weight: var(--font-weight-semibold);
                        margin-bottom: var(--space-1);
                    }
                    .po-info-item .value {
                        font-size: var(--font-size-base);
                        color: var(--color-text-primary);
                        font-weight: var(--font-weight-medium);
                    }
                    .summary-footer {
                        display: flex;
                        justify-content: flex-end;
                        gap: var(--space-6);
                        padding: var(--space-4) var(--space-3);
                        background: var(--color-bg-panel);
                        border-top: 2px solid var(--color-border);
                        font-size: var(--font-size-base);
                        font-weight: var(--font-weight-semibold);
                        color: var(--color-text-primary);
                    }
                    .jv-inline-input {
                        padding: var(--space-1) var(--space-2);
                        border: 1px solid var(--color-border);
                        border-radius: var(--radius-sm);
                        font-size: var(--font-size-sm);
                    }
                    .jv-inline-input:focus { outline: none; border-color: var(--color-brand-teal); }
                `}</style>

                {/* Tab Toggle */}
                <div className="text-center mb-3">
                    <div className="btn-group" role="group">
                        <Link
                            to="/purchasing"
                            className="btn btn-primary"
                            style={{ textDecoration: 'none' }}>
                            Purchasing
                        </Link>
                        <Link
                            to="/purchasing?tab=vendors"
                            className="btn btn-outline-primary"
                            style={{ textDecoration: 'none' }}>
                            Vendors
                        </Link>
                    </div>
                </div>

                <div style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-base)' }}>
                    <Link to="/purchasing" style={{ color: 'var(--color-brand-teal)', textDecoration: 'none' }}>
                        <i className="fa-sharp fa-regular fa-arrow-left mr-2"></i>Return to Purchase Orders
                    </Link>
                </div>

                {/* Header */}
                <div className="jv-card">
                    <h5 className="jv-card-title">
                        <i className="fa-sharp fa-regular fa-file-invoice mr-2"></i>
                        {po.po_number || `PO-${po.purchase_order_id}`}
                        <span style={{ marginLeft: 'var(--space-3)' }}><StatusBadge status={po.status} /></span>
                    </h5>

                    <div className="po-info-grid">
                        <div className="po-info-item">
                            <label>Vendor</label>
                            <div className="value">
                                {po.vendor_id ? (
                                    <Link to={`/vendors/${po.vendor_id}`} style={{ color: 'var(--color-brand-teal)', textDecoration: 'none' }}>
                                        {po.vendor_name || 'Unknown'}
                                    </Link>
                                ) : '—'}
                            </div>
                        </div>
                        <div className="po-info-item">
                            <label>Location</label>
                            <div className="value">{po.location_id ? getLocationName((locations || []).find(l => l.location_id === po.location_id) || {}) : 'All'}</div>
                        </div>
                        <div className="po-info-item">
                            <label>Expected Date</label>
                            <div className="value">
                                {editingHeader ? (
                                    <Form.Control type="date" size="sm"
                                        value={headerForm.expected_date || ''}
                                        onChange={(e) => this.setState(prev => ({ headerForm: { ...prev.headerForm, expected_date: e.target.value } }))} />
                                ) : (po.expected_date ? convertFromUTC(po.expected_date).format('MM/DD/YY') : '—')}
                            </div>
                        </div>
                        <div className="po-info-item">
                            <label>Created</label>
                            <div className="value">{po.created_at ? convertFromUTC(po.created_at).format('MM/DD/YY') : '—'}</div>
                        </div>
                    </div>

                    {editingHeader && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control as="textarea" rows={2}
                                    value={headerForm.notes || ''}
                                    onChange={(e) => this.setState(prev => ({ headerForm: { ...prev.headerForm, notes: e.target.value } }))} />
                            </Form.Group>
                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                <button className="jv-btn jv-btn-primary jv-btn-sm" onClick={this.handleSaveHeader} disabled={saving}>
                                    {saving ? <i className="fa-sharp fa-regular fa-spinner fa-spin"></i> : 'Save'}
                                </button>
                                <button className="jv-btn jv-btn-tertiary jv-btn-sm" onClick={() => this.setState({ editingHeader: false, headerForm: { ...po } })}>Cancel</button>
                            </div>
                        </>
                    )}

                    {!editingHeader && po.notes && (
                        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-base)', color: 'var(--color-text-secondary)' }}>
                            <strong>Notes:</strong> {po.notes}
                        </div>
                    )}

                    {/* Action Buttons */}
                    {!editingHeader && (
                        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-4)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border-light)', flexWrap: 'wrap' }}>
                            {isDraft && (
                                <button className="jv-btn jv-btn-secondary jv-btn-sm" onClick={() => this.setState({ editingHeader: true })}>
                                    <i className="fa-sharp fa-regular fa-pen mr-2"></i>Edit
                                </button>
                            )}
                            {isDraft && lines.length > 0 && (
                                <button className="jv-btn jv-btn-primary jv-btn-sm" onClick={this.handleSubmitPO} disabled={saving}>
                                    <i className="fa-sharp fa-regular fa-paper-plane mr-2"></i>Submit PO
                                </button>
                            )}
                            {lines.length > 0 && !isReadOnly && (
                                <button className="jv-btn jv-btn-tertiary jv-btn-sm" onClick={this.handleDownloadPdf}>
                                    <i className="fa-sharp fa-regular fa-file-pdf mr-2"></i>Download PDF
                                </button>
                            )}
                            {canReceive && (
                                <button className="jv-btn jv-btn-primary jv-btn-sm"
                                    onClick={() => this.setState({ showReceiveModal: true })}>
                                    <i className="fa-sharp fa-regular fa-box-check mr-2"></i>Receive
                                </button>
                            )}
                            {(isDraft || po.status === 'submitted') && (
                                <button className="jv-btn jv-btn-danger jv-btn-sm" onClick={this.handleCancelPO} disabled={saving}>
                                    <i className="fa-sharp fa-regular fa-ban mr-2"></i>Void PO
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Lines */}
                <div className="jv-card">
                    <div className="jv-card-header">
                        <h5 className="jv-card-title"><i className="fa-sharp fa-regular fa-list mr-2"></i>Line Items</h5>
                        {isDraft && (
                            <button className="jv-btn jv-btn-primary jv-btn-sm" onClick={() => this.setState({ addingLine: true })}>
                                <i className="fa-sharp fa-regular fa-plus mr-2"></i>Add Line
                            </button>
                        )}
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table className="jv-table">
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>Vendor SKU</th>
                                    <th>Qty Ordered</th>
                                    <th>Qty Received</th>
                                    <th>Unit Cost</th>
                                    <th>Line Total</th>
                                    {isDesktop && <th>Notes</th>}
                                    {!isReadOnly && <th style={{ width: '100px' }}></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map(line => {
                                    const isEditing = editingLineId === line.line_id;
                                    const lineTotal = (parseFloat(line.quantity_ordered) || 0) * (parseFloat(line.unit_cost) || 0);
                                    return (
                                        <tr key={line.line_id}>
                                            <td>
                                                {formatProductLabel(line) || '—'}{line.unit_quantity ? <small className="text-muted ml-1">({line.unit_quantity})</small> : ''}
                                            </td>
                                            <td>{line.vendor_sku || '—'}</td>
                                            <td>
                                                {isEditing ? (
                                                    <input className="jv-inline-input" type="number" min="1" style={{ width: '80px' }}
                                                        value={editLineForm.quantity_ordered}
                                                        onChange={(e) => this.setState(prev => ({ editLineForm: { ...prev.editLineForm, quantity_ordered: e.target.value } }))} />
                                                ) : line.quantity_ordered}
                                            </td>
                                            <td>{line.quantity_received || 0}</td>
                                            <td>
                                                {isEditing ? (
                                                    <input className="jv-inline-input" type="number" step="0.01" min="0" style={{ width: '100px' }}
                                                        value={editLineForm.unit_cost}
                                                        onChange={(e) => this.setState(prev => ({ editLineForm: { ...prev.editLineForm, unit_cost: e.target.value } }))} />
                                                ) : (line.unit_cost ? `$${parseFloat(line.unit_cost).toFixed(2)}` : '—')}
                                            </td>
                                            <td>{lineTotal > 0 ? `$${lineTotal.toFixed(2)}` : '—'}</td>
                                            {isDesktop && (
                                                <td>
                                                    {isEditing ? (
                                                        <input className="jv-inline-input" type="text" style={{ width: '120px' }}
                                                            value={editLineForm.notes}
                                                            onChange={(e) => this.setState(prev => ({ editLineForm: { ...prev.editLineForm, notes: e.target.value } }))} />
                                                    ) : (line.notes || '—')}
                                                </td>
                                            )}
                                            {!isReadOnly && (
                                                <td>
                                                    {isEditing ? (
                                                        <>
                                                            <button className="jv-btn-icon" onClick={this.handleSaveEditLine} disabled={saving} title="Save">
                                                                {saving ? <i className="fa-sharp fa-regular fa-spinner fa-spin"></i> : <i className="fa-sharp fa-regular fa-check"></i>}
                                                            </button>
                                                            <button className="jv-btn-icon" onClick={() => this.setState({ editingLineId: null })} title="Cancel">
                                                                <i className="fa-sharp fa-regular fa-xmark"></i>
                                                            </button>
                                                        </>
                                                    ) : isDraft ? (
                                                        <>
                                                            <button className="jv-btn-icon" onClick={() => this.handleStartEditLine(line)} title="Edit">
                                                                <i className="fa-sharp fa-regular fa-pen-to-square"></i>
                                                            </button>
                                                            <button className="jv-btn-icon" onClick={() => this.handleDeleteLine(line.line_id)} title="Remove" style={{ color: 'var(--color-danger)' }}>
                                                                <i className="fa-sharp fa-regular fa-trash"></i>
                                                            </button>
                                                        </>
                                                    ) : null}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                {addingLine && (
                                    <POLineEditor
                                        installerId={this.props.typeId}
                                        vendorId={po.vendor_id}
                                        onSave={this.handleAddLine}
                                        onCancel={() => this.setState({ addingLine: false })}
                                        saving={saving}
                                    />
                                )}
                            </tbody>
                        </table>

                        {lines.length === 0 && !addingLine && (
                            <div className="jv-empty-state">
                                <i className="fa-sharp fa-regular fa-list"></i>
                                <p>No line items. Add products to this purchase order.</p>
                            </div>
                        )}

                        {lines.length > 0 && (
                            <div className="summary-footer">
                                <span>Lines: {totals.totalLines}</span>
                                <span>Total Qty: {totals.totalQty}</span>
                                <span>Estimated Total: ${totals.totalCost.toFixed(2)}</span>
                            </div>
                        )}
                    </div>
                </div>


                {this.state.showReceiveModal && po && (
                    <ReceiveInventory
                        show={true}
                        onHide={() => this.setState({ showReceiveModal: false })}
                        onSuccess={() => { this.setState({ showReceiveModal: false }); this.loadData(); }}
                        initialPoId={po.purchase_order_id}
                        typeId={this.props.typeId}
                        selectedLocationId={this.props.selectedLocationId}
                        locations={this.props.locations}
                    />
                )}
            </div>
        );
    }
}

export default PODetail;
